package com.arena_web.ui.sections.profile.edit

import androidx.compose.runtime.*
import com.arena_web.data.dto.ProfileCardDto
import com.arena_web.data.dto.UserCardDto
import com.arena_web.domain.repositories.CardRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.events.PopupOverlayFadeIn
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.AvatarGenerationModal
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.overlay.ModalFadeInKeyframes
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * 3-step "Complete your profile" wizard launched from the People page when
 * the caller tries to swipe before their own profile is ready. Reuses the
 * existing Personal Info / Focus & Activities form bodies plus a simplified
 * card-style picker for step 3, all wrapped in a single shell that exposes
 * a "X/3" page indicator and a step-aware Cancel/Next/"Let's go!" footer.
 *
 * Each Next click commits that step's data through the matching callback
 * (which is wired to the existing ProfileViewModel update methods) so the
 * user's progress survives even if they close the wizard mid-flow.
 *
 * Step 3 owns the AvatarGenerationModal handoff — picking a template and
 * pressing "Create Photo" opens the existing modal layered on top; on Apply
 * the wizard reflects the freshly-generated avatar through [onAvatarConfirmed].
 */
@Composable
fun CompleteProfileWizardDialog(
    isOpen: Boolean,
    initialPersonalInfo: PersonalInfoFormState,
    initialFocus: FocusActivitiesFormState,
    ownedTemplates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    sportName: String,
    activeTemplateId: String?,
    hasGeneratedAvatar: Boolean,
    cardRepository: CardRepository?,
    pendingPurchase: UserCardDto?,
    purchasing: Boolean,
    purchaseError: String?,
    onClose: () -> Unit,
    onSavePersonalInfo: (PersonalInfoFormState) -> Unit,
    onSaveFocus: (FocusActivitiesFormState) -> Unit,
    onAvatarConfirmed: () -> Unit,
    onPurchaseRequest: (UserCardDto) -> Unit,
    onPurchaseConfirm: () -> Unit,
    onPurchaseCancel: () -> Unit,
    onReorderCards: (List<String>) -> Unit,
) {
    if (!isOpen) return

    val t = LocalAppStrings.current
    var stepIndex by remember { mutableStateOf(0) }
    // Step 1 / 2 form state — hoisted at wizard scope so Cancel + reopen
    // doesn't lose typed-but-unsaved changes within the same session.
    var personalInfo by remember(initialPersonalInfo) {
        mutableStateOf(initialPersonalInfo.copy(dateOfBirth = maskDob(initialPersonalInfo.dateOfBirth)))
    }
    var focus by remember(initialFocus) { mutableStateOf(initialFocus) }
    // Step 3 — selected template + open avatar modal target.
    val initialSelectedTemplate = activeTemplateId
        ?: ownedTemplates.firstOrNull { it.isOwned }?.id
        ?: ownedTemplates.firstOrNull()?.id
    var selectedTemplateId by remember(initialSelectedTemplate) {
        mutableStateOf(initialSelectedTemplate)
    }
    var avatarGenTemplateId by remember { mutableStateOf<String?>(null) }

    val totalSteps = 3
    val pageIndicator = "${stepIndex + 1}/$totalSteps"

    val isLastStep = stepIndex == totalSteps - 1
    val primaryLabel = if (isLastStep) t.profileWizardLetsGo else t.commonNext

    // Step-specific gating: don't let the user advance past step 1 with an
    // empty identity (matches the People feed completion gate) or past step
    // 2 without picking at least one interest of any kind.
    val canAdvance = when (stepIndex) {
        0 -> personalInfo.isValid()
        1 -> focus.totalSelected > 0
        else -> hasGeneratedAvatar
    }

    fun advance() {
        when (stepIndex) {
            0 -> {
                val committed = personalInfo.commit()
                personalInfo = committed
                onSavePersonalInfo(committed)
                stepIndex = 1
            }
            1 -> {
                onSaveFocus(focus)
                stepIndex = 2
            }
            else -> onClose()
        }
    }

    val (title, description) = when (stepIndex) {
        0 -> t.profileWizardStepPersonalInfoTitle to t.profileWizardStepPersonalInfoDescription
        1 -> t.profileWizardStepFocusTitle to t.profileWizardStepFocusDescription
        else -> t.profileWizardStepCardTitle to t.profileWizardStepCardDescription
    }

    val mobile = isMobile()

    // Step body — shared between the desktop modal and the mobile sheet. Steps
    // 1 & 2 reuse the same form bodies (the mobile sheet aliases `--scale` to
    // `--mobile-scale`, so the `s()`-sized fields render at the 390-px Figma
    // baseline); only step 3 reflows to a compact single-column card picker.
    val stepContent: @Composable () -> Unit = {
        when (stepIndex) {
            0 -> PersonalInfoFormBody(
                state = personalInfo,
                onStateChange = { personalInfo = it },
                singleColumn = mobile,
            )
            1 -> if (mobile) {
                Column(modifier = Modifier.fillMaxWidth().sGap(16)) {
                    SpanText(
                        t.profileWizardSelectedCount(focus.totalSelected),
                        modifier = Modifier
                            .sFontSize(16)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(24)
                            .sLetterSpacing(0.16)
                            .color(AppColors.Text.OnSurfaceMuted),
                    )
                    FocusActivitiesFormBody(state = focus, onStateChange = { focus = it })
                }
            } else {
                FocusActivitiesFormBody(state = focus, onStateChange = { focus = it })
            }
            else -> if (mobile) {
                MyStylesStepMobile(
                    ownedTemplates = ownedTemplates,
                    profileCards = profileCards,
                    sportName = sportName,
                    selectedTemplateId = selectedTemplateId,
                    onSelect = { selectedTemplateId = it },
                    onCreatePhoto = { id -> avatarGenTemplateId = id },
                    onLockedClick = onPurchaseRequest,
                    onReorderCards = onReorderCards,
                )
            } else {
                MyStylesStep(
                    ownedTemplates = ownedTemplates,
                    profileCards = profileCards,
                    sportName = sportName,
                    selectedTemplateId = selectedTemplateId,
                    onSelect = { selectedTemplateId = it },
                    onCreatePhoto = { id -> avatarGenTemplateId = id },
                    onLockedClick = onPurchaseRequest,
                    onReorderCards = onReorderCards,
                )
            }
        }
    }

    if (mobile) {
        WizardMobileSheet(
            title = title,
            pageIndicator = pageIndicator,
            stepIndex = stepIndex,
            primaryLabel = if (isLastStep) t.commonSave else t.commonNext,
            primaryEnabled = canAdvance,
            onClose = onClose,
            onBack = { if (stepIndex > 0) stepIndex -= 1 },
            onPrimary = { advance() },
            content = stepContent,
        )
    } else {
        WizardShell(
            title = title,
            description = description,
            pageIndicator = pageIndicator,
            onClose = onClose,
            onCancel = onClose,
            onPrimary = { advance() },
            primaryLabel = primaryLabel,
            primaryEnabled = canAdvance,
            content = stepContent,
        )
    }

    // Layered avatar generation — when "Create Photo" fires on step 3 we
    // open the existing AvatarGenerationModal on top of the wizard. Its
    // overlay has a higher z-index so the wizard stays mounted underneath
    // (no flicker on close), and Apply triggers `onAvatarConfirmed` which
    // refreshes the caller's profile so `hasGeneratedAvatar` becomes true
    // and "Let's go !" enables.
    val genId = avatarGenTemplateId
    if (genId != null && cardRepository != null) {
        val template = ownedTemplates.firstOrNull { it.id == genId }
        if (template != null) {
            AvatarGenerationModal(
                isOpen = true,
                onClose = { avatarGenTemplateId = null },
                cardRepository = cardRepository,
                cardId = template.id,
                mainProfileCard = profileCards.firstOrNull { it.userCardId == template.id },
                fallbackCard = template,
                sportName = sportName,
                onAvatarConfirmed = {
                    avatarGenTemplateId = null
                    onAvatarConfirmed()
                }
            )
        }
    }

    // Purchase-confirm modal — opens when the user taps a locked template
    // tile on step 3. Rendered after the wizard so it lands on top in DOM
    // order (both overlays use position:fixed; the later one wins).
    com.arena_web.ui.components.overlay.PurchaseCardModal(
        card = pendingPurchase,
        sportName = sportName,
        purchasing = purchasing,
        purchaseError = purchaseError,
        onConfirm = { _ -> onPurchaseConfirm() },
        onDismiss = { onPurchaseCancel() },
    )
}

/**
 * Custom shell for the wizard — mirrors [ProfileEditShell] but adds an
 * inline `X/3` page indicator next to the title and lets the caller drive
 * the primary button label per step (Next vs "Let's go !").
 */
@Composable
private fun WizardShell(
    title: String,
    description: String,
    pageIndicator: String,
    onClose: () -> Unit,
    onCancel: () -> Unit,
    onPrimary: () -> Unit,
    primaryLabel: String,
    primaryEnabled: Boolean,
    content: @Composable () -> Unit,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1000)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onClose() }
            .animation(
                PopupOverlayFadeIn.toAnimation(
                    duration = Constants.ANIMATION_FAST.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sBorderRadius(26)
                .onClick { it.stopPropagation() }
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property("width", "min(${s(700)}, 92vw)")
                    property("max-height", "90vh")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("background-color", EDIT_MODAL_SURFACE)
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
                .animation(
                    ModalFadeInKeyframes.toAnimation(
                        duration = Constants.ANIMATION_NORMAL.ms,
                        timingFunction = AnimationTimingFunction.EaseOut
                    )
                )
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sPadding(22)
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("scrollbar-width", "thin")
                        property("scrollbar-color", "rgba(255, 255, 255, 0.15) transparent")
                    }
            ) {
                // Header — title + "1/3" indicator + description on left,
                // Public-info badge on right; gradient hairline divider below.
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingBottom(12)
                        .styleModifier { property("align-items", "flex-end") },
                    verticalAlignment = Alignment.Bottom
                ) {
                    Column(
                        modifier = Modifier.flexGrow(1).sGap(8),
                    ) {
                        Row(verticalAlignment = Alignment.Bottom, modifier = Modifier.sGap(12)) {
                            SpanText(
                                title,
                                modifier = Modifier
                                    .sFontSize(24)
                                    .fontWeight(AppTypography.Medium)
                                    .sLineHeight(32)
                                    .sLetterSpacing(0.24)
                                    .color(AppColors.Text.Secondary)
                            )
                            SpanText(
                                pageIndicator,
                                modifier = Modifier
                                    .sFontSize(18)
                                    .fontWeight(AppTypography.SemiBold)
                                    .sLineHeight(18)
                                    .sLetterSpacing(0.018)
                                    .color(sportColors.primary)
                            )
                        }
                        SpanText(
                            description,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(AppTypography.Regular)
                                .sLineHeight(24)
                                .sLetterSpacing(0.16)
                                .color(AppColors.Text.OnSurfaceMuted)
                        )
                    }
                    PublicInfoBadge()
                }
                SectionGradientDivider()

                Box(modifier = Modifier.fillMaxWidth().sPaddingTop(32)) {
                    content()
                }

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sPaddingTop(24)
                        .sGap(12)
                        .styleModifier { property("justify-content", "flex-end") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    GhostPillButton(t.commonCancel, onClick = onCancel)
                    PrimaryOrGhostPillButton(
                        label = primaryLabel,
                        primary = primaryEnabled,
                        onClick = { if (primaryEnabled) onPrimary() },
                    )
                }
            }
        }
    }
}

@Composable
private fun PublicInfoBadge() {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier.sGap(8),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Icon(src = AppIcons.Globe, modifier = Modifier.sSize(16))
        SpanText(
            t.profileWizardPublicInfo,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Regular)
                .sLineHeight(22)
                .sLetterSpacing(0.028)
                .color(AppColors.Text.OnSurfaceMuted)
        )
    }
}

// =============================================================================
// Mobile shell — bottom sheet counterpart to [WizardShell] (Figma 8212:284768 /
// 8172:181380 / 8212:285253). Title + "X/3" indicator + Public badge in the
// header; a scrollable step body; a pinned footer whose left pill is "Cancel"
// on step 1 and "Back" thereafter, and whose right pill is "Next" → "Save".
// =============================================================================
@Composable
private fun WizardMobileSheet(
    title: String,
    pageIndicator: String,
    stepIndex: Int,
    primaryLabel: String,
    primaryEnabled: Boolean,
    onClose: () -> Unit,
    onBack: () -> Unit,
    onPrimary: () -> Unit,
    content: @Composable () -> Unit,
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    BottomSheet(onDismiss = onClose, contentGap = 16) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sGap(16)
                .styleModifier {
                    property("flex", "1 1 auto")
                    property("min-height", "0")
                }
        ) {
            // Header — title + "X/3" left, Public info badge right, divider under.
            Column(modifier = Modifier.fillMaxWidth().sGap(12)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        modifier = Modifier.flexGrow(1).sGap(12),
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        SpanText(
                            title,
                            modifier = Modifier
                                .sFontSize(20)
                                .fontWeight(AppTypography.Medium)
                                .sLineHeight(26)
                                .sLetterSpacing(0.2)
                                .color(AppColors.Text.Secondary)
                        )
                        SpanText(
                            pageIndicator,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(AppTypography.SemiBold)
                                .sLineHeight(18)
                                .sLetterSpacing(0.016)
                                .color(sportColors.primary)
                        )
                    }
                    PublicInfoBadge()
                }
                SectionGradientDivider()
            }

            // Scrollable body — the only part that grows/scrolls.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("flex", "1 1 auto")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("overflow-x", "hidden")
                        property("scrollbar-width", "none")
                        property("-ms-overflow-style", "none")
                    }
            ) {
                content()
            }

            // Footer — Cancel/Back + Next/Save as two equal-width pills.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SheetPillButton(
                    label = if (stepIndex == 0) t.commonCancel else t.commonBack,
                    primary = false,
                    enabled = true,
                    onClick = { if (stepIndex == 0) onClose() else onBack() },
                    modifier = Modifier.flexGrow(1),
                )
                SheetPillButton(
                    label = primaryLabel,
                    primary = true,
                    enabled = primaryEnabled,
                    onClick = { if (primaryEnabled) onPrimary() },
                    modifier = Modifier.flexGrow(1),
                )
            }
        }
    }
}

// =============================================================================
// Step 3 (mobile) — compact "My styles" picker (Figma 8212:285253 / 8212:285439).
// Selected card + position badge on the left, a horizontally-scrolling template
// strip on the right, a full-width Create Photo CTA, then the generated cards in
// an "Active profile cards" row once any avatar exists.
// =============================================================================
@Composable
private fun MyStylesStepMobile(
    ownedTemplates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    sportName: String,
    selectedTemplateId: String?,
    onSelect: (String) -> Unit,
    onCreatePhoto: (String) -> Unit,
    onLockedClick: (UserCardDto) -> Unit,
    onReorderCards: (List<String>) -> Unit,
) {
    val t = LocalAppStrings.current
    val selected = ownedTemplates.firstOrNull { it.id == selectedTemplateId }
    var localOrder by remember(profileCards) { mutableStateOf(profileCards) }
    val activeCards = localOrder.filter { !it.avatarUrl.isNullOrBlank() }
    var draggingId by remember { mutableStateOf<String?>(null) }
    val nextPosition = activeCards.size + 1

    Column(modifier = Modifier.fillMaxWidth().sGap(20)) {
        // "My styles" left, "Select to generate" right.
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                t.profileWizardMyStyles,
                modifier = Modifier
                    .flexGrow(1)
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.Text.Secondary)
            )
            SpanText(
                t.profileWizardSelectToGenerate,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(AppColors.Text.OnSurfaceMuted)
            )
        }

        if (ownedTemplates.isEmpty()) {
            SpanText(
                t.profileWizardNoTemplates,
                modifier = Modifier.sFontSize(14).color(AppColors.Text.OnSurfaceMuted)
            )
        } else {
            // Selected card | vertical divider | scrollable template strip.
            Row(
                modifier = Modifier.fillMaxWidth().sGap(12),
                verticalAlignment = Alignment.Top,
            ) {
                Column(
                    modifier = Modifier.sGap(8),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    PositionBadge("$nextPosition")
                    MobileCardArt(
                        template = selected,
                        sportName = sportName,
                        size = PlayerCardSize.Medium,
                        selectedBorder = true,
                    )
                }
                Box(
                    modifier = Modifier
                        .sWidth(1)
                        .sHeight(180)
                        .styleModifier {
                            property(
                                "background",
                                "linear-gradient(180deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                            )
                            property("flex-shrink", "0")
                        }
                )
                Div(attrs = {
                    style {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                        property("display", "flex")
                        property("gap", s(12))
                        property("align-items", "flex-start")
                        property("padding-top", s(4))
                        property("overflow-x", "auto")
                        property("scrollbar-width", "none")
                    }
                }) {
                    ownedTemplates.forEach { template ->
                        MobileStripThumb(
                            template = template,
                            sportName = sportName,
                            isSelected = template.id == selectedTemplateId,
                            onClick = {
                                if (template.isOwned) onSelect(template.id)
                                else onLockedClick(template)
                            },
                        )
                    }
                }
            }

            Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                CreatePhotoButton(
                    enabled = selected != null,
                    onClick = { selected?.let { onCreatePhoto(it.id) } },
                )
            }
        }

        if (activeCards.isNotEmpty()) {
            SectionGradientDivider()
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    t.profileWizardActiveCards,
                    modifier = Modifier
                        .flexGrow(1)
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .sLetterSpacing(0.09)
                        .color(AppColors.Text.Secondary)
                )
                SpanText(
                    t.profileWizardDragToReorder,
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .color(AppColors.Text.OnSurfaceMuted)
                )
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(12)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("row-gap", s(12))
                    },
                verticalAlignment = Alignment.Top,
            ) {
                activeCards.forEachIndexed { index, card ->
                    Column(
                        modifier = Modifier.sGap(8),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        PositionBadge("${index + 1}")
                        ActiveProfileCardThumb(
                            card = card,
                            sportName = sportName,
                            isDragging = draggingId == card.id,
                            onDragStart = { draggingId = card.id },
                            onDragEnd = { draggingId = null },
                            onDragOver = {
                                val draggedId = draggingId ?: return@ActiveProfileCardThumb
                                if (draggedId == card.id) return@ActiveProfileCardThumb
                                val draggedCard = activeCards.firstOrNull { it.id == draggedId }
                                    ?: return@ActiveProfileCardThumb
                                val newOrder = localOrder.toMutableList()
                                val fromIdx = newOrder.indexOfFirst { it.id == draggedId }
                                val toIdx = newOrder.indexOfFirst { it.id == card.id }
                                if (fromIdx < 0 || toIdx < 0) return@ActiveProfileCardThumb
                                newOrder.removeAt(fromIdx)
                                newOrder.add(toIdx, draggedCard)
                                localOrder = newOrder
                                onReorderCards(newOrder.map { it.id })
                            },
                        )
                    }
                }
            }
        }
    }
}

// Small circular position badge — translucent glass disc with a number,
// shown above the selected style and each active card (Figma 8171:174894).
@Composable
private fun PositionBadge(label: String) {
    Box(
        modifier = Modifier
            .sSize(24)
            .sBorderRadius(100)
            .styleModifier {
                property("background", AppGradients.InteractiveSecondary)
                property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                property("box-sizing", "border-box")
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            label,
            modifier = Modifier
                .sFontSize(14)
                .fontWeight(AppTypography.Medium)
                .sLineHeight(18)
                .sLetterSpacing(0.09)
                .color(AppColors.Text.Secondary)
        )
    }
}

// Card art sized to a [PlayerCardSize] slot, optionally framed with the blue
// "selected" border. Used for the selected style preview on mobile step 3.
@Composable
private fun MobileCardArt(
    template: UserCardDto?,
    sportName: String,
    size: PlayerCardSize,
    selectedBorder: Boolean = false,
) {
    Box(
        modifier = Modifier
            .sBorderRadius(14)
            .styleModifier {
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
                property("padding", s(6))
                if (selectedBorder) {
                    property("border", "0.5px solid #0061FF")
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.2) 100%)"
                    )
                } else {
                    property("border", "0.5px solid transparent")
                }
            },
        contentAlignment = Alignment.Center,
    ) {
        if (template != null) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = size,
                avatarUrl = null,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = template.assetsBasePath,
                cardTextColor = template.textColor,
            )
        }
    }
}

// Template tile in the mobile strip — XtraSmall card, blue border when selected,
// dimmed + lock badge when not owned.
@Composable
private fun MobileStripThumb(
    template: UserCardDto,
    sportName: String,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    val locked = !template.isOwned
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("flex-shrink", "0")
                property("position", "relative")
            },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier.styleModifier { if (locked) property("opacity", "0.55") },
            contentAlignment = Alignment.Center,
        ) {
            MobileCardArt(
                template = template,
                sportName = sportName,
                size = PlayerCardSize.XtraSmall,
                selectedBorder = isSelected && !locked,
            )
        }
        if (locked) {
            Box(
                modifier = Modifier
                    .sSize(24)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("position", "absolute")
                        property("background", "rgba(11, 22, 46, 0.8)")
                        property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                        property("backdrop-filter", "blur(4px)")
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText("🔒", modifier = Modifier.sFontSize(12))
            }
        }
    }
}

// =============================================================================
// Step 3 — simplified "My styles" picker. Mirrors Figma 7688:306657: selected
// template renders large on the left with a Create Photo CTA, owned templates
// flow as smaller tiles on the right. No drag-to-reorder here — that lives in
// the full BringCardToLifePopup on the Profile page.
// =============================================================================
@Composable
private fun MyStylesStep(
    ownedTemplates: List<UserCardDto>,
    profileCards: List<ProfileCardDto>,
    sportName: String,
    selectedTemplateId: String?,
    onSelect: (String) -> Unit,
    onCreatePhoto: (String) -> Unit,
    onLockedClick: (UserCardDto) -> Unit,
    onReorderCards: (List<String>) -> Unit,
) {
    val t = LocalAppStrings.current
    val selected = ownedTemplates.firstOrNull { it.id == selectedTemplateId }

    // Local optimistic order — drag-and-drop mutates this immediately so the
    // UI feels snappy. The full list (including any avatar-less placeholders)
    // is preserved so the backend reorder takes a complete card-id list.
    // Each successful drop flushes through `onReorderCards` right away so the
    // wizard step doesn't need teardown bookkeeping to persist intent.
    var localOrder by remember(profileCards) { mutableStateOf(profileCards) }
    val activeCards = localOrder.filter { !it.avatarUrl.isNullOrBlank() }
    var draggingId by remember { mutableStateOf<String?>(null) }

    Column(modifier = Modifier.fillMaxWidth().sGap(24)) {
        // Section header — "My styles" left, "Select to generate" right.
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                t.profileWizardMyStyles,
                modifier = Modifier
                    .flexGrow(1)
                    .sFontSize(18)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(22)
                    .sLetterSpacing(0.09)
                    .color(AppColors.Text.Secondary)
            )
            SpanText(
                t.profileWizardSelectToGenerate,
                modifier = Modifier
                    .sFontSize(14)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(22)
                    .sLetterSpacing(0.028)
                    .color(AppColors.Text.OnSurfaceMuted)
            )
        }

        if (ownedTemplates.isEmpty()) {
            SpanText(
                t.profileWizardNoTemplates,
                modifier = Modifier
                    .sFontSize(14)
                    .color(AppColors.Text.OnSurfaceMuted)
            )
        } else {
        Row(
            modifier = Modifier.fillMaxWidth().sGap(16),
            verticalAlignment = Alignment.Top,
        ) {
            // Selected card + Create Photo CTA.
            Column(
                modifier = Modifier
                    .sGap(20)
                    .styleModifier { property("padding", "0 ${s(12)}") },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SelectedTemplatePreview(template = selected, sportName = sportName)
                CreatePhotoButton(
                    enabled = selected != null,
                    onClick = { selected?.let { onCreatePhoto(it.id) } },
                )
            }

            // Vertical hairline divider.
            Box(
                modifier = Modifier
                    .sWidth(1)
                    .sHeight(280)
                    .styleModifier {
                        property(
                            "background",
                            "linear-gradient(180deg, rgba(148, 149, 152, 0) 0%, rgba(148, 149, 152, 0.3) 50%, rgba(148, 149, 152, 0) 100%)"
                        )
                        property("flex-shrink", "0")
                    }
            )

            // Remaining templates as a wrapping row of tiles.
            Row(
                modifier = Modifier
                    .flexGrow(1)
                    .sGap(12)
                    .sPaddingTop(16)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("align-content", "flex-start")
                        property("row-gap", s(12))
                    },
            ) {
                ownedTemplates.forEach { template ->
                    TemplateThumb(
                        template = template,
                        sportName = sportName,
                        isSelected = template.id == selectedTemplateId,
                        onClick = {
                            if (template.isOwned) onSelect(template.id)
                            else onLockedClick(template)
                        },
                    )
                }
            }
        }
        }   // else (templates not empty)

        // Active profile cards — appears once the user has at least one
        // generated avatar so they can see the cards they've already created.
        // Until then the section stays hidden and "Let's go !" stays disabled.
        if (activeCards.isNotEmpty()) {
            SectionGradientDivider()
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    t.profileWizardActiveCards,
                    modifier = Modifier
                        .flexGrow(1)
                        .sFontSize(18)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(22)
                        .sLetterSpacing(0.09)
                        .color(AppColors.Text.Secondary)
                )
                SpanText(
                    t.profileWizardCardCount(activeCards.size),
                    modifier = Modifier
                        .sFontSize(14)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(22)
                        .sLetterSpacing(0.028)
                        .color(AppColors.Text.OnSurfaceMuted)
                )
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(12)
                    .styleModifier {
                        property("flex-wrap", "wrap")
                        property("row-gap", s(12))
                    },
                verticalAlignment = Alignment.Top,
            ) {
                activeCards.forEach { card ->
                    ActiveProfileCardThumb(
                        card = card,
                        sportName = sportName,
                        isDragging = draggingId == card.id,
                        onDragStart = { draggingId = card.id },
                        onDragEnd = { draggingId = null },
                        onDragOver = {
                            val draggedId = draggingId ?: return@ActiveProfileCardThumb
                            if (draggedId == card.id) return@ActiveProfileCardThumb
                            // Translate active-subset indices to full-list
                            // indices so avatar-less placeholders keep their
                            // relative position in the saved order.
                            val draggedCard = activeCards.firstOrNull { it.id == draggedId }
                                ?: return@ActiveProfileCardThumb
                            val newOrder = localOrder.toMutableList()
                            val fromIdx = newOrder.indexOfFirst { it.id == draggedId }
                            val toIdx = newOrder.indexOfFirst { it.id == card.id }
                            if (fromIdx < 0 || toIdx < 0) return@ActiveProfileCardThumb
                            newOrder.removeAt(fromIdx)
                            newOrder.add(toIdx, draggedCard)
                            localOrder = newOrder
                            onReorderCards(newOrder.map { it.id })
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun ActiveProfileCardThumb(
    card: ProfileCardDto,
    sportName: String,
    isDragging: Boolean,
    onDragStart: () -> Unit,
    onDragEnd: () -> Unit,
    onDragOver: () -> Unit,
) {
    // Compose-html Div lets us bind HTML5 drag events directly (Kobweb's Box
    // doesn't expose drag callbacks). The visual is built with the same
    // PlayerCard primitive used elsewhere.
    Div(attrs = {
        attr("draggable", "true")
        style {
            property("width", s(115))
            property("height", s(173))
            property("flex-shrink", "0")
            property("position", "relative")
            property("cursor", "grab")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            if (isDragging) property("opacity", "0.4")
        }
        onDragStart { onDragStart() }
        onDragEnd { onDragEnd() }
        onDragEnter { it.preventDefault(); onDragOver() }
        onDragOver { it.preventDefault() }
    }) {
        PlayerCard(
            playerName = "",
            rating = 0,
            position = "",
            sport = sportName,
            size = PlayerCardSize.Arena,
            avatarUrl = card.avatarUrl,
            hideRating = true,
            hideShadow = true,
            cardAssetsBasePath = card.assetsBasePath,
            cardTextColor = card.textColor,
        )
        // Drag-handle marker — 6-dot grid (Figma 7777:451944). The 115-wide
        // thumb centers the 110-wide PlayerCard, so card-right is 2.5px from
        // Div-right → right offset = 9.5 to sit ~7px inside the card edge.
        Icon(
            src = AppIcons.DragHandle,
            modifier = Modifier
                .sWidth(10)
                .sHeight(14)
                .styleModifier {
                    property("position", "absolute")
                    property("top", s(0))
                    property("right", s(10))
                },
        )
    }
}

@Composable
private fun SelectedTemplatePreview(template: UserCardDto?, sportName: String) {
    // Borderless preview matching the BringCardToLifePopup main slot — no
    // frame, more breathing room. Scaled PlayerCard.Large so the inner art
    // stays crisp.
    val scale = 0.8
    val nativeWidth = 254
    val nativeHeight = 385
    Box(
        modifier = Modifier
            .sWidth((nativeWidth * scale).toInt())
            .sHeight((nativeHeight * scale).toInt())
            .styleModifier {
                property("position", "relative")
                property("flex-shrink", "0")
            },
        contentAlignment = Alignment.Center
    ) {
        if (template != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("width", s(nativeWidth))
                    property("height", s(nativeHeight))
                    property("transform", "scale($scale)")
                    property("transform-origin", "top left")
                }
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = sportName,
                    size = PlayerCardSize.Large,
                    avatarUrl = null,
                    hideRating = true,
                    hideShadow = true,
                    cardAssetsBasePath = template.assetsBasePath,
                    cardTextColor = template.textColor,
                )
            }
        }
    }
}

@Composable
private fun TemplateThumb(
    template: UserCardDto,
    sportName: String,
    isSelected: Boolean,
    onClick: () -> Unit,
) {
    val locked = !template.isOwned
    Box(
        modifier = Modifier
            .sWidth(115)
            .sHeight(173)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property("flex-shrink", "0")
                property("box-sizing", "border-box")
                property("position", "relative")
                if (isSelected && !locked) {
                    property("border", "1px solid #0c42ff")
                    property("border-radius", s(14))
                    property(
                        "background",
                        "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.12) 100%)"
                    )
                } else {
                    property("border", "1px solid transparent")
                }
            },
        contentAlignment = Alignment.Center
    ) {
        Box(
            modifier = Modifier.styleModifier {
                if (locked) property("opacity", "0.55")
            },
            contentAlignment = Alignment.Center
        ) {
            PlayerCard(
                playerName = "",
                rating = 0,
                position = "",
                sport = sportName,
                size = PlayerCardSize.Arena,
                avatarUrl = null,
                hideRating = true,
                hideShadow = true,
                cardAssetsBasePath = template.assetsBasePath,
                cardTextColor = template.textColor,
            )
        }
        if (locked) {
            // Centered lock badge — matches the Card Shop / BringCardToLifePopup
            // tile so the user can read "locked → tap to buy".
            Box(
                modifier = Modifier
                    .sSize(32)
                    .sBorderRadius(100)
                    .styleModifier {
                        property("position", "absolute")
                        property("background", "rgba(11, 22, 46, 0.8)")
                        property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                        property("backdrop-filter", "blur(4px)")
                    },
                contentAlignment = Alignment.Center
            ) {
                SpanText("🔒", modifier = Modifier.sFontSize(14))
            }
        }
    }
}

@Composable
private fun CreatePhotoButton(enabled: Boolean, onClick: () -> Unit) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    Box(
        modifier = Modifier
            .sHeight(46)
            .sBorderRadius(100)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .styleModifier {
                property("position", "relative")
                property("background-color", "#0061FF")
                if (!enabled) property("opacity", "0.55")
            }
            .onClick { if (enabled) onClick() },
        contentAlignment = Alignment.Center,
    ) {
        Row(
            modifier = Modifier
                .sGap(8)
                .styleModifier {
                    property("padding", "${s(14)} ${s(28)}")
                    property("display", "inline-flex")
                    property("align-items", "center")
                },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Icon(src = AppIcons.AiSparkle, modifier = Modifier.sSize(24))
            SpanText(
                t.profileWizardCreatePhoto,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Regular)
                    .sLineHeight(24)
                    .sLetterSpacing(0.16)
                    .color(sportColors.onPrimary)
            )
        }
    }
}
